import React, { useState, useEffect } from 'react';
import cardsData from './Data.js';
import Card from './Card';
import GameOver from './GameOver.js';

function Board({ onShowScores, username }) {
    // instanceId permet à React de correctement identifier et re-rendre les cartes lors du shuffle
    const [cards, setCards] = useState([]);
    const [firstCard, setFirstCard] = useState(null);
    const [secondCard, setSecondCard] = useState(null);
    // isChecking empêche les clics pendant la comparaison (délai de 600ms)
    const [isChecking, setIsChecking] = useState(false);
    const [moves, setMoves] = useState(0);
    const [matchedPairs, setMatchedPairs] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [time, setTime] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    // On duplique et mélange les cartes une seule fois au montage
    // Chaque carte a un instanceId unique (basé sur son index) pour que React les suive correctement
    useEffect(() => {
        const duplicated = [...cardsData, ...cardsData];

        const prepared = duplicated.map((card, index) => ({
            ...card,
            instanceId: index,
            isFlipped: false,
            isMatched: false,
        }));

        // Math.random() - 0.5 n'est pas un bon shuffle (Fisher-Yates serait meilleur)
        // mais suffit pour un jeu casual
        prepared.sort(() => Math.random() - 0.5);
        setCards(prepared);
    }, []);

    function handleCardClick(clickedCard) {
        // Ignorer les clics si le jeu est en pause, en cours de vérification, ou si la partie est finie
        if (isPaused) return;
        // Le timer démarre au premier clic (lazy start)
        if (!isTimerRunning) {
            setIsTimerRunning(true);
        }
        if (isGameOver) return;
        if (isChecking) return;
        // Une carte déjà retournée ne peut pas être cliquée à nouveau
        if (clickedCard.isFlipped) return;

        // Retourner la carte cliquée dans une copie du tableau
        const updated = cards.map((card) =>
            card.instanceId === clickedCard.instanceId ? { ...card, isFlipped: true } : card
        );
        setMoves((prev) => prev + 1);
        setCards(updated);

        // Version mise à jour de la carte cliquée
        const flipped = updated.find((c) => c.instanceId === clickedCard.instanceId);

        // Sélection de la 1ère ou 2ème carte
        if (!firstCard) setFirstCard(flipped);
        else if (!secondCard) setSecondCard(flipped);
    }

    useEffect(() => {
        let interval;

        if (isTimerRunning && !isPaused) {
            interval = setInterval(() => {
                setTime((prev) => prev + 1);
            }, 1000);
        }

        if (isGameOver) {
            setIsTimerRunning(false);
        }

        return () => clearInterval(interval);
    }, [isTimerRunning, isGameOver, isPaused]);

    // Comparaison automatique dès que 2 cartes sont sélectionnées
    // Le délai de 600ms donne au joueur le temps de voir les deux cartes
    useEffect(() => {
        if (firstCard && secondCard) {
            setIsChecking(true);
            const timer = setTimeout(() => {
                let updated;

                // Si les cartes matchent : on les marque comme trouvées
                if (firstCard.id === secondCard.id) {
                    setMatchedPairs((prev) => prev + 1);
                    updated = cards.map((card) =>
                        card.instanceId === firstCard.instanceId || card.instanceId === secondCard.instanceId
                            ? { ...card, isMatched: true }
                            : card
                    );
                }

                // Sinon : on les retourne à nouveau après le délai
                else {
                    updated = cards.map((card) =>
                        card.instanceId === firstCard.instanceId || card.instanceId === secondCard.instanceId
                            ? { ...card, isFlipped: false }
                            : card
                    );
                }

                const allMatched = updated.every((card) => card.isMatched);

                setCards(updated);
                setFirstCard(null);
                setSecondCard(null);
                setIsChecking(false);
                if (allMatched) {
                    setIsGameOver(true);
                    saveScore({
                        coups: moves,
                        time,
                    });
                }
            }, 600);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstCard, secondCard, cards]);

    // Redémarrer une nouvelle partie (mêmes étapes que l'initialisation)
    function restartGame() {
        const duplicated = [...cardsData, ...cardsData];

        const prepared = duplicated.map((card, index) => ({
            ...card,
            instanceId: index,
            isFlipped: false,
            isMatched: false,
        }));

        prepared.sort(() => Math.random() - 0.5);

        // reset complet
        setCards(prepared);
        setFirstCard(null);
        setSecondCard(null);
        setMoves(0);
        setMatchedPairs(0);
        setIsChecking(false);
        setIsGameOver(false);
        setTime(0);
        setIsTimerRunning(false);
    }

    function saveScore({ coups, time }) {
        // Le score est d'abord envoyé au backend (MongoDB via l'API)
        // Si l'API échoue, on stocke en localStorage comme fallback
        // Cela permet de jouer hors ligne ou si le serveur est down
        const scoreData = {
            pseudo: username || 'Anonyme',
            coups,
            time,
        };

        // Détecter l'environnement selon l'hôte
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const API_URL = isLocalhost
            ? 'http://localhost:3001/api/scores'
            : 'https://memory-game-test-technique-rodium.onrender.com/api/scores'; 

        // Essayer d'envoyer à l'API backend
        fetch(API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(scoreData),
        })
            .then((response) => {
                if (response.ok) {
                    // Score sauvegardé avec succès
                } else {
                    // Fallback sur localStorage
                    saveScoreToLocalStorage(scoreData);
                }
            })
            .catch((error) => {
                // Fallback sur localStorage
                saveScoreToLocalStorage(scoreData);
            });
    }

    function saveScoreToLocalStorage({ pseudo, coups, time }) {
        const stored = JSON.parse(localStorage.getItem('scores')) || [];

        const newScore = {
            pseudo,
            coups,
            time,
            date: new Date().toISOString(),
        };

        const allScores = [...stored, newScore];

        // Tri par : coups (asc) > temps (asc) > date (asc)
        // Les meilleurs scores (moins de coups, moins de temps) apparaissent en premier
        allScores.sort((a, b) => {
            if (a.coups !== b.coups) return a.coups - b.coups; // moins de coups
            if (a.time !== b.time) return a.time - b.time; // moins de temps
            return new Date(a.date) - new Date(b.date); // date ancienne → haute
        });

        localStorage.setItem('scores', JSON.stringify(allScores));
    }

    // Affichage de l'écran de fin de partie
    if (isGameOver) {
        return <GameOver moves={moves} matchedPairs={matchedPairs} time={time} onRestart={restartGame} />;
    }

    // Affichage du plateau (4x4)
    return (
        <div className="game-container">
            <div className="stats-wrapper">
                <div className="game-stats">
                    <p>Coups : {moves}</p>
                    <p>Paires : {matchedPairs}/8</p>
                    <p>Temps : {time}s</p>
                </div>
            </div>
            {isPaused && (
                <div className="pause-overlay">
                    <p>⏸ Pause</p>
                </div>
            )}
            <div className="board-wrapper">
                <div className="board">
                    {cards.map((card) => (
                        <Card key={card.instanceId} card={card} onClick={handleCardClick} />
                    ))}
                </div>
            </div>

            <button className="replay-btn" onClick={restartGame}>
                Rejouer
            </button>
            <button className="score-btn" onClick={onShowScores}>
                Voir les scores
            </button>

            {!isPaused ? (
                <button className="pause-btn" onClick={() => setIsPaused(true)}>
                    ⏸️
                </button>
            ) : (
                <button className="reprendre-btn" onClick={() => setIsPaused(false)}>
                    ▶️
                </button>
            )}
        </div>
    );
}

export default Board;
